import React from "react";
import PropTypes from "prop-types";

/**
 * SOF-62 subscription inclusions: an optional gift voucher amount and an
 * optional percentage discount applied to the client's future product
 * purchases. Both are optional, so an empty field means "no inclusion".
 */
const SubscriptionInclusionsField = ({
  voucherAmount = "",
  productDiscountPercentage = "",
  onVoucherAmountChange,
  onProductDiscountChange,
  errors = {},
}) => (
  <div className="subscription-inclusions-field">
    <h4 className="fw-semibold mb-2">Inclusions</h4>
    <div className="row">
      <label className="col-md-6 mb-2">
        Gift voucher amount
        <input
          className="form-control"
          type="text"
          inputMode="decimal"
          name="voucher_amount"
          placeholder="No voucher"
          value={voucherAmount}
          onChange={(event) => onVoucherAmountChange(event.target.value)}
        />
        {errors.voucher_amount ? (
          <small className="text-danger">{errors.voucher_amount}</small>
        ) : null}
      </label>
      <label className="col-md-6 mb-2">
        Product discount (%)
        <input
          className="form-control"
          type="text"
          inputMode="decimal"
          name="product_discount_percentage"
          placeholder="No discount"
          value={productDiscountPercentage}
          onChange={(event) => onProductDiscountChange(event.target.value)}
        />
        {errors.product_discount_percentage ? (
          <small className="text-danger">{errors.product_discount_percentage}</small>
        ) : null}
      </label>
    </div>
    <small className="text-muted d-block mb-2">
      Applied to this client&apos;s future product purchases at the point of sale.
    </small>
  </div>
);

const voucherMinor = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") {
    const minor = Number(value.amount_minor);
    return Number.isFinite(minor) && minor > 0 ? minor : null;
  }
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : null;
};

export const formatInclusionVoucherAmount = (value, currency = "AUD") => {
  const minor = voucherMinor(value);
  if (minor === null) return "";
  const resolvedCurrency = (
    typeof value === "object" && value?.currency ? value.currency : currency
  );
  return `${resolvedCurrency} ${(minor / 100).toFixed(2)}`;
};

export const SubscriptionInclusionsSummary = ({
  voucherAmount,
  productDiscountPercentage,
  currency = "AUD",
}) => {
  const voucherText = formatInclusionVoucherAmount(voucherAmount, currency);
  const percentageValue = Number(productDiscountPercentage);
  const hasDiscount = productDiscountPercentage !== null
    && productDiscountPercentage !== undefined
    && productDiscountPercentage !== ""
    && Number.isFinite(percentageValue)
    && percentageValue > 0;

  if (!voucherText && !hasDiscount) return null;

  return (
    <div className="mb-2">
      <h6>Inclusions</h6>
      <ul className="list-unstyled mb-0">
        {voucherText ? <li>Gift voucher: {voucherText}</li> : null}
        {hasDiscount ? (
          <li>Product discount: {percentageValue}% off future product purchases</li>
        ) : null}
      </ul>
    </div>
  );
};

SubscriptionInclusionsField.propTypes = {
  voucherAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  productDiscountPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onVoucherAmountChange: PropTypes.func.isRequired,
  onProductDiscountChange: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
};

SubscriptionInclusionsSummary.propTypes = {
  voucherAmount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
  productDiscountPercentage: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  currency: PropTypes.string,
};

export default SubscriptionInclusionsField;
